import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { maskCustomerId } from '@/lib/maskCustomerId';
import { PICKER_SEARCH_THRESHOLD, matchesPickerSearch, sortPickerCandidates } from '@/lib/adsPickerList';
import type { AdsPickerCandidate } from '@/hooks/useAdsConnectFlow';

export interface AdsAccountPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: AdsPickerCandidate[];
  onConfirm: (adsAccountId: string) => void;
  isLinking: boolean;
}

/**
 * Konto zur aktiven Merchant Center zuordnen (Spec 4.3 Punkt 2). Kandidaten
 * kommen aus der Datenbank, nicht aus dem Callback-Body (im Vollbild-Pfad gibt
 * es keinen). MCC und Zeilen mit Verknuepfung zu einer ANDEREN MC sind sichtbar,
 * aber nicht waehlbar: kein stilles Umhaengen, kein Pull ins Leere.
 *
 * AUF 150 KANDIDATEN AUSGELEGT (Nachtrag 12.09.2026). Ein MCC-Grant wie
 * google@oma.ag fuehrt rund 150 Ads-Konten. Die erste Fassung war eine
 * ungedeckelte Liste in der Reihenfolge der Datenbank: der Dialog wuchs aus dem
 * Sichtfenster heraus, Kopf und Fussleiste waren unerreichbar, und der Nutzer
 * konnte weder bestaetigen noch schliessen. Drei Dinge zusammen machen ihn
 * wieder bedienbar -- Hoehendeckel plus EIN scrollender Bereich, ein Suchfeld
 * ab der neunten Zeile, und eine Reihenfolge, die den Vorschlag nach oben holt.
 */
export function AdsAccountPickerDialog({ open, onOpenChange, candidates, onConfirm, isLinking }: AdsAccountPickerDialogProps) {
  const { t } = useTranslation('optimization');
  const [query, setQuery] = useState('');
  const sorted = useMemo(() => sortPickerCandidates(candidates), [candidates]);
  // Die Vorauswahl haengt an ALLEN waehlbaren Zeilen, nicht an den gerade
  // sichtbaren: sonst raeumte jeder Tastendruck im Suchfeld die Auswahl weg.
  const selectable = useMemo(() => candidates.filter((c) => c.selectable), [candidates]);
  const visible = useMemo(() => sorted.filter((c) => matchesPickerSearch(c, query)), [sorted, query]);
  const [picked, setPicked] = useState<string | null>(selectable.length === 1 ? selectable[0].id : null);

  // Die Vorauswahl haengt an einer Liste, die NACHTRAEGLICH ankommen kann:
  // `useAdsAccounts` laedt asynchron, und der Dialog ist schon vorher montiert
  // (er haengt an `pickerOpen`, nicht an den Daten). Ein reiner Startwert in
  // `useState` haette das einzige waehlbare Konto dann nie vorausgewaehlt --
  // genau in dem Fall, fuer den die Vorauswahl da ist. Dasselbe gilt fuer den
  // Vorschlag aus der Merchant-Center-Verknuepfung: der trifft ueber einen
  // eigenen Netzweg ein, also immer nach dem ersten Rendern.
  //
  // Die Regel ist bewusst eng: eine getroffene Auswahl wird NIE durch eine
  // andere ersetzt, auch nicht durch einen eintreffenden Vorschlag. Faellt sie
  // aus der Liste (fremd verknuepft, geloescht), steht der Dialog wieder auf
  // "nichts gewaehlt" und der Knopf ist gesperrt -- automatisch auf ein
  // Nachbarkonto zu springen hiesse, im schlimmsten Fall ein anderes Konto zu
  // verknuepfen, als der Nutzer angeklickt hat.
  //
  // Vorgeschlagen wird nur bei GENAU EINEM waehlbaren Vorschlag. Bei mehreren
  // waere jede Wahl geraten, und raten ist hier schlechter als fragen.
  useEffect(() => {
    setPicked((current) => {
      if (current) return selectable.some((c) => c.id === current) ? current : null;
      const suggested = selectable.filter((c) => c.suggested);
      if (suggested.length === 1) return suggested[0].id;
      return selectable.length === 1 ? selectable[0].id : null;
    });
  }, [selectable]);

  // BEIDE Aufrufer rendern diesen Dialog unbedingt und haengen nur seinen
  // Inhalt an `open` (AdsChapterRow, GoogleAdsAccountRow). Ohne dieses
  // Aufraeumen begruesst der naechste Aufruf den Nutzer mit einem vorbefuellten
  // Suchfeld und einer gefilterten Liste, ohne dass er je gesucht haette.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const showSearch = candidates.length > PICKER_SEARCH_THRESHOLD;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `max-h` plus `flex-col`: die Karte darf nicht hoeher werden als das
          Sichtfenster, und ihre drei Teile teilen sich die Hoehe im Fluss auf,
          damit nur die Liste scrollt und Kopf wie Fussleiste stehen bleiben. */}
      <DialogContent data-testid="ads-picker" className="flex max-h-[85vh] flex-col">
        {/* `shrink-0` an allen drei starren Bereichen: die Liste hat
            `flex-basis: 0` und damit den skalierten Schrumpffaktor 0 -- ohne
            diese Tokens gaebe Flexbox zuerst hier nach statt bei ihr. */}
        <DialogHeader data-testid="ads-picker-header" className="shrink-0">
          <DialogTitle className="font-heading">{t('feedCheck.ads.picker.title')}</DialogTitle>
          <DialogDescription>{t('feedCheck.ads.picker.description')}</DialogDescription>
        </DialogHeader>
        {showSearch && (
          <Input
            data-testid="ads-picker-search"
            className="shrink-0"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('feedCheck.ads.picker.search')}
            aria-label={t('feedCheck.ads.picker.search')}
          />
        )}
        {/* `min-h-0` ist der Teil, den man beim Nachbauen vergisst: ein
            Flex-Kind haelt sonst seine Inhaltshoehe, und der Ueberlauf wandert
            zurueck an die Karte -- also genau dorthin, wo er nicht hin soll. */}
        <div data-testid="ads-picker-list" className="min-h-0 flex-1 overflow-y-auto">
          <RadioGroup value={picked ?? ''} onValueChange={setPicked} className="flex flex-col gap-2">
            {visible.map((c) => {
              // Ohne `descriptive_name` traegt die Zeile nur die maskierte
              // Kundennummer -- zweimal dieselbe Nummer nebeneinander sieht aus
              // wie zwei Angaben und ist eine.
              const masked = maskCustomerId(c.adsCustomerId);
              const label = [c.name ?? masked, c.name ? masked : null, c.currency]
                .filter((part): part is string => !!part)
                .join(' · ');
              // `aria-label` SCHLAEGT das zugeordnete `<label>` in der
              // Namensberechnung, die Hinweiszeile darunter erreicht einen
              // Screenreader also nie. Beim Vorschlag ist das die teuerste
              // Luecke: die Zeile ist waehlbar UND vorausgewaehlt, und ohne
              // Grund hoert man einen vorausgewaehlten Radiobutton ohne jede
              // Erklaerung. Die beiden anderen Hinweise sagt `disabled` mit an.
              const accessibleName = c.suggested
                ? `${label} · ${t('feedCheck.ads.picker.suggested')}`
                : label;
              return (
                <div key={c.id} className="flex items-start gap-3 rounded-md border border-border/60 p-3">
                  <RadioGroupItem value={c.id} id={`ads-pick-${c.id}`} disabled={!c.selectable} aria-label={accessibleName} />
                  <label htmlFor={`ads-pick-${c.id}`} className="flex flex-col text-sm">
                    <span className={c.selectable ? 'font-medium' : 'font-medium text-muted-foreground'}>{label}</span>
                    {c.suggested && <span className="text-xs text-primary">{t('feedCheck.ads.picker.suggested')}</span>}
                    {c.linkedElsewhere && <span className="text-xs text-muted-foreground">{t('feedCheck.ads.picker.linkedElsewhere')}</span>}
                    {!c.selectable && !c.linkedElsewhere && <span className="text-xs text-muted-foreground">{t('feedCheck.ads.picker.manager')}</span>}
                  </label>
                </div>
              );
            })}
          </RadioGroup>
          {/* Eine leere Liste ohne ein Wort dazu liest sich wie ein
              Ladefehler. Der Satz steht INNERHALB des Scrollbereichs, damit
              die Karte beim Tippen nicht in der Hoehe springt. */}
          {visible.length === 0 && (
            <p className="px-1 py-3 text-sm text-muted-foreground">{t('feedCheck.ads.picker.noMatch')}</p>
          )}
        </div>
        <DialogFooter data-testid="ads-picker-footer" className="shrink-0">
          <Button
            data-testid="ads-picker-confirm"
            disabled={!picked || isLinking}
            onClick={() => picked && onConfirm(picked)}
          >
            {isLinking ? t('feedCheck.ads.picker.linking') : t('feedCheck.ads.picker.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
